import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Clock, Share2, Tag } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface TournamentCardProps {
  tournament: {
    id: string;
    title: string;
    organizer?: string;
    sport: string;
    date: string;
    endDate?: string;
    location: string;
    registrationFee: number;
    genderCategories?: string[];
    subcategories?: Record<string, string[]>;
    imageUrl?: string;
    registrationDeadline?: string;
    matchType?: 'singles' | 'dobles' | 'ambos';
    address?: string;
    postal_code?: string;
  };
  onTournamentClick?: (tournament: any) => void;
}

const TournamentCard = ({ tournament, onTournamentClick }: TournamentCardProps) => {
  const { t, language } = useLanguage();
  const dateLocaleStr = language === 'es' ? 'es-ES' : 'en-US';

  const getDefaultTournamentImage = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'pádel':
      case 'padel':
        return '/lovable-uploads/ae890168-70e4-43bb-976d-e01eb8869c31.png';
      case 'tenis':
        return '/lovable-uploads/c9c992c0-9cbd-430e-9c26-ed615c5e47dc.png';
      case 'pickleball':
        return '/lovable-uploads/404ad11f-3a8c-4971-8c7f-b2f397498282.png';
      default:
        return '/lovable-uploads/bb567a48-6ee6-4eea-917e-c2e42d492ea0.png';
    }
  };

  const formatEventDate = (dateString: string, endDateString?: string) => {
    const startDate = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    
    if (endDateString) {
      const endDate = new Date(endDateString);
      const startFormatted = startDate.toLocaleDateString(dateLocaleStr, { day: 'numeric', month: 'short' });
      const endFormatted = endDate.toLocaleDateString(dateLocaleStr, options);
      return `${startFormatted} - ${endFormatted}`;
    }
    
    return startDate.toLocaleDateString(dateLocaleStr, options);
  };

  const calculateDaysUntilDeadline = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDeadlineDisplay = () => {
    if (!tournament.registrationDeadline) return null;
    
    const daysUntil = calculateDaysUntilDeadline(tournament.registrationDeadline);
    
    if (daysUntil < 0) {
      return { text: t('events.closed'), color: "bg-gray-500" };
    } else if (daysUntil === 0) {
      return { text: t('events.lastDay'), color: "bg-red-500" };
    } else if (daysUntil <= 3) {
      return { text: `${daysUntil} ${daysUntil > 1 ? t('events.daysLeft') : t('events.dayLeft')}`, color: "bg-red-500" };
    } else if (daysUntil <= 7) {
      return { text: `${daysUntil} ${t('events.daysLeft')}`, color: "bg-orange-500" };
    } else {
      return { text: `${daysUntil} ${t('events.daysLeft')}`, color: "bg-green-500" };
    }
  };

  const translateCategory = (cat: string) => {
    const normalized = cat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const exact = t(`categories.${normalized}`);
    if (exact !== `categories.${normalized}`) return exact;
    const prefixes = ['masculino', 'femenino', 'mixto'];
    for (const prefix of prefixes) {
      if (normalized.startsWith(prefix)) {
        return t(`categories.${prefix}`);
      }
    }
    return cat;
  };

  const formatCategories = () => {
    let cats = tournament.genderCategories || [];
    // Fallback: use subcategories keys if genderCategories is empty
    if (cats.length === 0 && tournament.subcategories && Object.keys(tournament.subcategories).length > 0) {
      cats = Object.keys(tournament.subcategories);
    }
    if (cats.length === 0) {
      return `${t('categories.masculino')}, ${t('categories.femenino')}`;
    }
    const unique = [...new Set(cats.map(translateCategory))];
    return unique.join(", ");
  };

  const normalizedSport = (tournament.sport || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const isDoubles = tournament.matchType === 'dobles' || normalizedSport === 'padel';
  const displayPrice = isDoubles ? Math.round(tournament.registrationFee / 2) : tournament.registrationFee;

  const handleCardClick = () => {
    if (onTournamentClick) {
      onTournamentClick(tournament);
    }
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const eventUrl = `${window.location.origin}/event/${tournament.id}`;
    const shareText = `¡Únete a ${tournament.title}! ${tournament.location} - ${formatEventDate(tournament.date)}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${eventUrl}`)}`;
    
    if (navigator.share) {
      navigator.share({
        title: tournament.title,
        text: shareText,
        url: eventUrl,
      }).catch(console.error);
    } else {
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleRegisterClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTournamentClick) {
      onTournamentClick(tournament);
    }
  };

  const deadlineDisplay = getDeadlineDisplay();
  const imageUrl = tournament.imageUrl || getDefaultTournamentImage(tournament.sport);

  return (
    <Card 
      className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer bg-white border-gray-200 group p-3 md:p-4"
      onClick={handleCardClick}
    >
      <div className="flex gap-3 md:gap-4">
        {/* Event Image - LEFT SIDE with overlaid badges */}
        <div className="relative flex-shrink-0">
          {/* Status + Sport Badge Container */}
          <div className="absolute top-2 left-2 z-10 flex gap-1">
            {deadlineDisplay && deadlineDisplay.text !== t('events.closed') && (
              <Badge className={`${deadlineDisplay.color} text-white text-[10px] px-1.5 py-0.5`}>
                {t('events.upcoming')}
              </Badge>
            )}
            <Badge className="bg-white/90 text-gray-800 text-[10px] px-1.5 py-0.5">
              {tournament.sport}
            </Badge>
          </div>
          <img 
            src={imageUrl} 
            alt={tournament.title}
            className="w-28 h-28 md:w-32 md:h-32 rounded-lg object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>

        {/* Event Information - RIGHT SIDE */}
        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
          {/* Header: Título + Precio */}
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-sm md:text-base line-clamp-2 leading-tight text-gray-900">
                {tournament.title}
              </h3>
              {tournament.organizer && (
                <p className="text-[10px] md:text-xs text-muted-foreground truncate">
                  {t('events.byOrganizer')} {tournament.organizer}
                </p>
              )}
            </div>
            {/* Precio destacado */}
            <div className="text-right flex-shrink-0">
              <span className="text-base md:text-lg font-bold text-primary">
                ${displayPrice.toLocaleString('es-MX')}
              </span>
              <span className="text-[10px] md:text-xs text-muted-foreground block">
                {t('events.perPerson')}
              </span>
            </div>
          </div>

          {/* Detalles del evento */}
          <div className="space-y-0.5 text-xs md:text-sm text-muted-foreground">
            {/* Fechas */}
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3 md:w-3.5 md:h-3.5 text-[#3D6B4F] flex-shrink-0" />
              <span className="truncate">{formatEventDate(tournament.date, tournament.endDate)}</span>
            </div>
            
            {/* Límite de inscripción */}
            {deadlineDisplay && (
              <div className="flex items-center gap-1.5">
                <Clock className="w-3 h-3 md:w-3.5 md:h-3.5 text-orange-500 flex-shrink-0" />
                <span>
                  {t('events.deadline')}: <span className={`font-medium ${deadlineDisplay.text === t('events.closed') ? "text-gray-500" : deadlineDisplay.text.includes(t('events.dayLeft')) && parseInt(deadlineDisplay.text) <= 3 ? "text-red-600" : "text-gray-700"}`}>
                    {deadlineDisplay.text}
                  </span>
                </span>
              </div>
            )}
            
            {/* Ubicación */}
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3 h-3 md:w-3.5 md:h-3.5 text-green-500 flex-shrink-0" />
              <span className="truncate">{tournament.location}</span>
            </div>
          </div>

          {/* Footer: Categorías + Botones */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <Badge variant="outline" className="text-[10px] md:text-xs px-1.5 py-0 truncate max-w-[120px] md:max-w-[180px]">
              <Tag className="w-2.5 h-2.5 mr-1 flex-shrink-0" />
              {formatCategories()}
            </Badge>
            <Button 
              size="sm" 
              className="h-7 text-xs px-2 md:px-3 bg-primary hover:bg-primary/90"
              onClick={handleRegisterClick}
            >
              {t('common.register')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TournamentCard;
